import React, { useState } from 'react';
import { User, Trash2, FileText, Send } from 'lucide-react';
import { COLORS } from '../../../../utils/constants';

const NotesTab = ({ details, newNote, setNewNote, addNote, onDeleteNote }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter to show only manual notes (from employees, not system generated)
  // Uses the 'source' flag from backend - notes with source='system' are auto-generated
  const isAutoGenerated = (note) => {
    // Check if note has explicit system flag (set by backend)
    if (note.source === 'system' || note.auto_generated === true || note.type === 'system' || note.is_system === true) {
      return true;
    }
    return false;
  };

  const allNotes = details?.notes || [];
  const manualNotes = allNotes.filter(note => !isAutoGenerated(note));

  const handleAddNote = async () => {
    if (!newNote?.trim() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      await addNote();
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote();
    }
  };

  return (
    <div>
      {/* Add Note Form */}
      <div style={{ 
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: COLORS.background,
        borderRadius: '10px',
      }}>
        <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '8px' }}>
          Добавить заметку о клиенте
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <textarea
            value={newNote || ''}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Напишите заметку..."
            rows={2}
            style={{
              flex: 1,
              padding: '12px 14px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              resize: 'vertical',
              minHeight: '60px',
            }}
          />
          <button
            onClick={handleAddNote}
            disabled={!newNote?.trim() || isSubmitting}
            style={{
              padding: '12px 20px',
              backgroundColor: newNote?.trim() && !isSubmitting ? COLORS.primary : COLORS.border,
              border: 'none',
              borderRadius: '8px',
              color: newNote?.trim() && !isSubmitting ? '#000' : COLORS.textSecondary,
              fontWeight: '600',
              cursor: newNote?.trim() && !isSubmitting ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              alignSelf: 'flex-end',
            }}
          >
            <Send size={16} />
            {isSubmitting ? 'Добавление...' : 'Добавить'}
          </button>
        </div>
      </div>

      {/* Info about filtering */}
      <div style={{ 
        marginBottom: '16px',
        padding: '10px 14px',
        backgroundColor: `${COLORS.primary}10`,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <FileText size={16} color={COLORS.primary} />
        <span style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
          Заметки сотрудников ({manualNotes.length})
          {allNotes.length > manualNotes.length && (
            <span style={{ marginLeft: '4px', opacity: 0.7 }}>
              • Скрыто автоматических: {allNotes.length - manualNotes.length}
            </span>
          )}
        </span>
      </div>
      
      {/* Notes List */}
      {manualNotes.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {manualNotes.map((note, i) => (
            <div key={note.id || i} style={{
              padding: '14px',
              backgroundColor: COLORS.card,
              borderRadius: '10px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: `${COLORS.primary}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <User size={16} color={COLORS.primary} />
                  </div>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: '13px', fontWeight: '600' }}>
                      {note.manager_name || note.author || 'Сотрудник'}
                    </div>
                    <div style={{ color: COLORS.textSecondary, fontSize: '11px' }}>
                      {note.created_at ? new Date(note.created_at).toLocaleString('ru-RU') : 'Без даты'}
                    </div>
                  </div>
                </div>
                {onDeleteNote && (
                  <button
                    onClick={() => onDeleteNote(note.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: COLORS.textSecondary,
                      cursor: 'pointer',
                      padding: '4px',
                      opacity: 0.6,
                    }}
                    title="Удалить заметку"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
              <div style={{ 
                color: COLORS.text, 
                fontSize: '14px', 
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {note.note || note.text || note.content}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: COLORS.background,
          borderRadius: '10px',
        }}>
          <FileText size={32} style={{ color: COLORS.textSecondary, marginBottom: '12px', opacity: 0.5 }} />
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Нет заметок от сотрудников
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginTop: '4px' }}>
            Добавьте первую заметку о клиенте
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesTab;
